import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import { Card, Notice } from '@ailab/ui';
import { checkoutSchema } from '@ailab/contracts';
import { useMe } from '../../lib/account';
import { apiRequest, errorMessage } from '../../lib/http';
import { CHECKOUT_STATUS_TEXT } from './TopUpSection';
import styles from './account.module.css';

// Vuelta desde Stripe (RF-02.04). Los créditos los añade el webhook, no esta página: aquí
// solo se consulta el estado hasta que el servidor lo confirma.

const credits = new Intl.NumberFormat('es-ES');
const euros = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' });
const POLL_MS = 2000;
const POLL_LIMIT_MS = 2 * 60 * 1000;
const SESSION_ID = /^cs_[A-Za-z0-9_]{1,200}$/;

export function TopUpReturnPage() {
  const search: { checkout?: unknown } = useSearch({ strict: false });
  const sessionId = typeof search.checkout === 'string' ? search.checkout : '';
  const valid = SESSION_ID.test(sessionId);
  const me = useMe();
  const client = useQueryClient();
  const status = useQuery({
    queryKey: ['billing', 'checkout', sessionId],
    queryFn: () => apiRequest(`/me/billing/checkouts/${sessionId}`, checkoutSchema),
    enabled: valid && Boolean(me.data),
    refetchInterval: (query) => {
      const data = query.state.data;
      const waiting = !data || data.status === 'open' || data.status === 'processing';
      return waiting && Date.now() - query.state.dataUpdatedAt < POLL_LIMIT_MS ? POLL_MS : false;
    },
  });
  const credited = status.data?.status === 'credited';

  useEffect(() => {
    if (credited) {
      void client.invalidateQueries({ queryKey: ['me', 'balance'] });
      void client.invalidateQueries({ queryKey: ['me', 'movements'] });
    }
  }, [credited, client]);

  const back = (
    <Link className={styles.inlineLink} to="/cuenta">
      Volver a tu cuenta
    </Link>
  );

  if (!valid) {
    return (
      <Card className={styles.narrow}>
        <Notice tone="error">No encontramos esa recarga.</Notice>
        {back}
      </Card>
    );
  }
  if (me.isPending || status.isPending) return <p role="status">Comprobando tu pago…</p>;
  if (status.isError) return <Notice tone="error">{errorMessage(status.error)}</Notice>;

  const data = status.data;
  const message = {
    open: 'Estamos esperando la confirmación de Stripe. Puede tardar unos segundos.',
    processing:
      'Tu banco todavía está confirmando el pago. Te añadiremos los créditos en cuanto llegue.',
    credited: `Hemos añadido ${credits.format(data.credits)} créditos a tu cuenta.`,
    expired: 'La página de pago caducó sin completarse. No se ha cobrado nada.',
    failed: 'El pago no se ha completado. No se ha cobrado nada.',
    review:
      'Hemos recibido el pago, pero administración tiene que revisarlo antes de añadir los créditos.',
  }[data.status];

  return (
    <>
      <h1>Recarga</h1>
      <Card className={styles.narrow}>
        <h2 className={styles.sectionTitle}>{CHECKOUT_STATUS_TEXT[data.status]}</h2>
        <Notice tone={credited ? 'success' : data.status === 'review' ? 'error' : 'info'}>
          {message}
        </Notice>
        {data.amountTotalCents !== null ? (
          <p className={styles.muted}>
            Pagado: {euros.format(data.amountTotalCents / 100)}
            {data.amountTaxCents
              ? `, de los que ${euros.format(data.amountTaxCents / 100)} son IVA`
              : ''}
            .
          </p>
        ) : null}
        {back}
      </Card>
    </>
  );
}
